import { useEffect } from 'react';
import { useToastStore, Toast as ToastType } from '../../stores/toastStore';
import { SuccessIcon, ErrorIcon, WarningIcon, InfoIcon, CloseIcon } from '../icons';
import './Toast.css';

/**
 * Individual toast notification component.
 */
interface ToastItemProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const Icon = {
    success: SuccessIcon,
    error: ErrorIcon,
    warning: WarningIcon,
    info: InfoIcon,
  }[toast.type];

  return (
    <div
      className={`toast toast-${toast.type}`}
      role="alert"
      aria-live="polite"
    >
      <span className="toast-icon">
        <Icon size={20} />
      </span>
      <div className="toast-content">
        <span className="toast-title">{toast.title}</span>
        {toast.message && <span className="toast-message">{toast.message}</span>}
      </div>
      {toast.dismissible && (
        <button
          className="toast-dismiss"
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss notification"
        >
          <CloseIcon size={16} />
        </button>
      )}
    </div>
  );
}

/**
 * Toast container component that renders all active toasts.
 * Should be placed at the root of the application.
 * 
 * Requirements: 10.4
 */
export function ToastContainer() {
  const { toasts, removeToast } = useToastStore();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all toasts when component unmounts
    };
  }, []);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="toast-container" aria-label="Notifications">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
      ))}
    </div>
  );
}
